import { useState } from 'react';
import { CaretIcon, CheckmarkIcon, NodeBoothIcon } from '../assets/icons';
import type { OrgNode } from '../features/organizations/organizationsApi';

/** Selection keys are namespaced so a node id and an area id never collide. */
export const nodeKey = (id: number) => `n:${id}`;
export const areaKey = (id: number) => `a:${id}`;
/** The numeric id encoded in a selection key. */
export const keyId = (key: string) => Number(key.split(':')[1]);

/** Label for a node: "Level · Name" when a level is known, else just the name. */
const nodeLabel = (n: OrgNode) =>
  [n.level_name, n.org_name].filter(Boolean).join(' · ') || n.org_name || `Node ${n.id}`;

/** All selection keys beneath a node (child nodes, their areas, and its own areas). */
export function descendantsOf(node: OrgNode): string[] {
  const out: string[] = [];
  (node.areas ?? []).forEach((a) => {
    if (a.id != null) out.push(areaKey(a.id));
  });
  (node.children ?? []).forEach((c) => {
    if (c.id != null) out.push(nodeKey(c.id));
    out.push(...descendantsOf(c));
  });
  return out;
}

/** Flatten the tree to a selection-key → label map (nodes and booth areas). */
export function orgNodeLabels(nodes: OrgNode[]): Record<string, string> {
  const map: Record<string, string> = {};
  const walk = (n: OrgNode) => {
    if (n.id != null) map[nodeKey(n.id)] = nodeLabel(n);
    (n.areas ?? []).forEach((a) => {
      if (a.id != null) map[areaKey(a.id)] = a.booth_name || a.area_code || `Booth ${a.id}`;
    });
    (n.children ?? []).forEach(walk);
  };
  nodes.forEach(walk);
  return map;
}

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={[
        'flex size-[18px] shrink-0 items-center justify-center rounded transition-colors',
        checked ? 'bg-brand text-white' : 'border border-[#cbd5e1] bg-white',
      ].join(' ')}
    >
      {checked && <CheckmarkIcon className="size-3.5" />}
    </span>
  );
}

/**
 * Org hierarchy tree (like step-4 "Internal Organization"): nodes carry a level
 * badge + name; the booth areas under a node are shown and selectable too. Both
 * nodes and booths are individually checkable — the parent owns the selection set
 * (keyed by `nodeKey`/`areaKey`) via `onToggle`, so it supports single/multi.
 */
export default function OrgNodeTree({
  nodes,
  selected,
  onToggle,
}: {
  nodes: OrgNode[];
  selected: Set<string>;
  /** `descendantKeys` are the keys under this item — drop them when selecting a parent. */
  onToggle: (key: string, label: string, descendantKeys: string[]) => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const toggle = (id: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const Tick = () => (
    <span className="absolute -left-[16px] top-1/2 h-px w-3 -translate-y-1/2 bg-[#e2e8f0]" />
  );

  const renderNode = (node: OrgNode, depth: number, ancestorSelected: boolean) => {
    const id = node.id;
    if (id == null) return null;
    const kids = node.children ?? [];
    const areas = node.areas ?? [];
    const hasChildren = kids.length > 0 || areas.length > 0;
    const open = !collapsed.has(id);
    const key = nodeKey(id);
    const isSel = !ancestorSelected && selected.has(key);
    // A selected node (or a selected ancestor) disables everything beneath it.
    const childrenLocked = ancestorSelected || isSel;

    const select = () => onToggle(key, nodeLabel(node), descendantsOf(node));

    return (
      <div key={key}>
        <div className="relative">
          {depth > 0 && <Tick />}
          <div
            className={[
              'flex items-center gap-2',
              isSel ? 'rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-2.5 py-1.5' : 'py-1.5',
              ancestorSelected ? 'opacity-50' : '',
            ].join(' ')}
          >
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggle(id)}
                aria-label={open ? 'Collapse' : 'Expand'}
                className="flex size-4 shrink-0 items-center justify-center text-[#64748b]"
              >
                <CaretIcon className={['size-4 transition-transform', open ? '' : '-rotate-90'].join(' ')} />
              </button>
            ) : (
              <span className="size-4 shrink-0" />
            )}
            <button
              type="button"
              onClick={select}
              disabled={ancestorSelected}
              className="shrink-0 disabled:cursor-not-allowed"
            >
              <Checkbox checked={isSel} />
            </button>
            {node.level_name && (
              <span className="shrink-0 rounded bg-[#e5eeff] px-1.5 py-0.5 text-[10px] font-medium text-brand">
                {node.level_name}
              </span>
            )}
            <button
              type="button"
              onClick={ancestorSelected ? () => toggle(id) : select}
              className={[
                'flex-1 truncate text-left text-sm',
                isSel ? 'font-semibold text-brand' : 'font-medium text-ink',
              ].join(' ')}
            >
              {node.org_name}
            </button>
          </div>
        </div>

        {hasChildren && open && (
          <div className="ml-[7px] flex flex-col border-l border-[#e2e8f0] pl-[16px]">
            {kids.map((c) => renderNode(c, depth + 1, childrenLocked))}
            {areas.map((a, i) => {
              if (a.id == null) return null;
              const akey = areaKey(a.id);
              const aSel = !childrenLocked && selected.has(akey);
              const label = a.booth_name || a.area_code || `Booth ${a.id}`;
              return (
                <div key={`${akey}-${i}`} className="relative">
                  <Tick />
                  <button
                    type="button"
                    disabled={childrenLocked}
                    onClick={() => onToggle(akey, label, [])}
                    className={[
                      'flex w-full items-center gap-2 text-left disabled:cursor-not-allowed',
                      aSel ? 'rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-2.5 py-1.5' : 'py-1.5',
                      childrenLocked ? 'opacity-50' : '',
                    ].join(' ')}
                  >
                    <Checkbox checked={aSel} />
                    <NodeBoothIcon className="size-4 shrink-0 text-brand" />
                    <span
                      className={[
                        'flex-1 truncate text-sm',
                        aSel ? 'font-semibold text-brand' : 'font-normal text-ink-secondary',
                      ].join(' ')}
                    >
                      {label}
                    </span>
                    {a.area_code && a.area_code !== label && (
                      <span className="shrink-0 text-[10px] text-ink-secondary">{a.area_code}</span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (nodes.length === 0) {
    return <p className="px-3 py-2 text-sm text-ink-secondary">No levels available.</p>;
  }

  return <div className="flex flex-col">{nodes.map((n) => renderNode(n, 0, false))}</div>;
}
